import React, { useState } from "react";
import Project from "../view/projectView";
import DesignPrototypeView from "../view/designPrototypeView";

function ProjectPresenter({ projectType = "technical" }) {
  const [expanded, setExpanded] = useState({}); // To track expanded state for each project

  const toggleDescription = (index) => {
    setExpanded((prev) => ({
      ...prev,
      [index]: !prev[index], // Toggle only the clicked project
    }));
  };

  // Define the projects array
  const projects = [
    {
      image: "/teori_app.png",
      title: "Driving Theory App",
      languages: "React, TypeScript, Figma",
      description:
        "MVP for a driving school (summer 2025). Delivers a complete driving theory book with a clean web UI. Some images are AI-generated; a few pages omit images. Deployed on Vercel.",
      link: "https://teoriapp.vercel.app/",
      type: "technical",
    },
    {
      image: "/web.png",
      title: "Augmented Reality Advertising",
      languages: "Three.js, React, Blender, Figma",
      description:
        "This project is part of my bachelor thesis, which explores how Augmented Reality (AR) can improve the visibility and user experience of web-based advertisements. I developed an interactive AR prototype using React and Three.js, showcasing side-placed 3D ads in a web environment.",
      link: "https://github.com/KimNG02/Web-AR-Vite",
      type: "technical",
    },
    {
      image: "/popcorn.png",
      title: "PopcornPick",
      languages: "React, Javascript, Figma and Firebase Firestore",
      description:
        "PopcornPick is a movie rating web application where users can browse, search, rate, and review movies. The project is built using Vite, React, and Firebase, following the Model-View-Presenter (MVP) design pattern. The development team consists of me and three other students from the DH2642 Interaction Programming and the Dynamic Web course. As part of the team, I helped develop the app and took the lead in designing its Figma prototype, where I applied my knowledge of design principles, such as consistency, usability, and user-centered design, to ensure a seamless and engaging user experience.",
      link: "https://dh2642-popcorn-pick.web.app/",
      type: "technical",
    },
    {
      image: "/skin.png",
      title: "Skincare app",
      languages: "TypeScript, Tailwind CSS, supabase",
      description:
        "This is an app designed for skincare enthusiasts to scan products and receive personalized information based on their unique skin conditions, acting like a knowledgeable friend in your pocket for informed skincare choices. Developed collaboratively with a friend during our free time, I focused on designing the app's interface and contributing to the frontend, while my friend worked on both frontend and backend development. This project enhanced our technical skills and deepened our understanding of user-centric app development, combining functionality with a seamless user experience.",
      link: "https://github.com/KimNG02/Skincare-app-",
      type: "technical",
    },
    {
      image: "/sproutView.png",
      title: "Sprout View",
      languages: "Javascript, Java and Dockerfile",
      description:
        "Sproutview is a web app that allows users to choose their favorite plant and see how it grows under different conditions, such as environment, light, weather, temperature, or soil. I collaborated with seven other classmates on this project. We used Scrum and Agile methodologies, working from 8 AM to 5 PM every weekday, and completed the project in one month.",
      link: "https://github.com/KimNG02/SproutView_II1305",
      type: "technical",
    },

    {
      images: [
        "/Front_page_alternative_1.png",
        "/Logga_in.png",
        "/product.png",
      ],
      title: "Skincare App – Draft Prototype",
      languages: "Figma",
      description:
        "This is a mobile app prototype for personalized skincare guidance, created as a personal project during summer 2024. The concept helps users check whether a skincare product is suitable for their skin by scanning or entering the product’s ingredients. The app analyzes the ingredients together with the user’s skin type and skin concerns to provide a personalized suitability recommendation.\n\nI designed the full UX/UI, onboarding flow, and low-fidelity prototype in Figma, then conducted surveys with over 200 people to better understand users’ skincare needs, habits, and pain points.",
      link: "https://www.figma.com/design/GQh0M8WagmiW3BwHQh69JJ/Skincare-app-draft?node-id=0-1&t=6FwPGAvGVPNFWOu4-1",
      type: "design",
    },

    {
      images: [
        "/Intro_1.png",
        "/Intro_2.png",
        "/Intro_3.png",
        "/log_in_main.png",
        "/home_main.png",
        "/Kvittohantering.png",

        // Add more images here, for example: "/ekonomi-page-2.png",
      ],
      title: "EkonomiHjälpen - High-fidelity prototype ",
      languages: "Figma",
      description:
        "EkonomiHjälpen is a digital prototype developed during the DH1622 (Human-Computer Interaction, Introductory Course) at KTH to simplify financial management for small and family-owned businesses. I took the lead in designing the high-fidelity prototype using Figma, transforming insights from user research and paper prototypes into an interactive and user-friendly design. The app focuses on streamlining receipt and invoice management by offering AI-powered receipt scanning, categorization, and tools for tracking and automating invoices while ensuring secure data handling compliant with GDPR. Using the Double Diamond methodology, our team conducted user research, ideation sessions, and iterative prototyping, with usability testing at each stage to refine navigation, interactivity, and functionality. ",
      link: "https://www.figma.com/proto/PqsuNZKn9Zf2ENFjbuloQ3/Prototyp-HCI?node-id=3-2&starting-point-node-id=3%3A2",
      type: "design",
    },
    {
      images: [
        "/Frame_1.png",
        "/Frame_2.png",
        "/Frame_3.png",
        "/Frame_4.png",
        // Add more images here, for example: "/finfy-page-2.png",
      ],
      title: "FinFy - Low-fidelity prototype",
      languages: "Figma",
      description:
        "During the summer of 2024, I attended the SEB Entrepreneur Camp, where my team and I pitched our idea, Finfy, an investment app designed to make financial literacy more accessible for both children and adults. To support our presentation, I created a quick prototype in just a few hours to visually demonstrate the app’s functionality and user experience, showcasing its potential to simplify investing for families. This experience strengthened my skills in rapid prototyping, communicating product ideas, and presenting effectively to an audience.",
      link: "https://www.figma.com/design/Fu4pEwmXXDSTja76StqdTk/Untitled?node-id=0-1&p=f&t=ig7w49xu9CvBlK58-0",
      type: "design",
    },

    {
      images: ["/Home.png", "/Detail_page.png", "/Review.png", "/About_Us.png"],
      title: "PopcornPick - Low-fidelity prototype",
      languages: "Figma",
      description:
        "This is a web app created for the course DH2642 Interaction Programming and the Dynamic Web. Before development started, I took the role of designing the prototype in Figma, focusing on consistency, usability, and user-centered design principles.",
      link: "https://www.figma.com/design/CRqqjOGuvm85hfTLH7HPwx/PopcornPick?node-id=0-1&t=jYKQq2XVn0bcdZhs-1",
      type: "design",
    },
    {
      images: [
        "/Framsida_(huvudversion).png",
        "/Projekt1.png",
        "/Projekt2.png",
        "/Projekt3.png",
        "/Baksida.png",

        // Add more images here, for example: "/portfolio-page-2.png",
      ],
      title: "My portfolio - prototype",
      languages: "Figma",
      description: "One of my portfolio designs.",
      link: "https://www.figma.com/design/ehtbpTLBAPvCmOVuMjNhf6/Min-portfolio?node-id=0-1&p=f&t=2vhJj0ePcvLwlzso-0",
      type: "design",
    },
  ];

  const visibleProjects = projects.filter(
    (project) => project.type === projectType,
  );
  if (projectType === "design") {
    return (
      <DesignPrototypeView
        projectData={visibleProjects}
        expanded={expanded}
        toggleDescription={toggleDescription}
      />
    );
  }

  return (
    <Project
      title="My Technical Projects"
      projectData={visibleProjects}
      expanded={expanded}
      toggleDescription={toggleDescription}
    />
  );
}

export default ProjectPresenter;
